// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the Workshop's saved views
//  ---------------------------------------------------------------------------
//  How somebody has chosen to LOOK at their projects: as a list, as a board of
//  columns, or as spans on a timeline — with what filtered out, what sorted by,
//  what grouped into columns, and which fields on show.
//
//  ── THE LENS MOVED OUT; THE PROJECTS STAYED ───────────────────────────────
//  This file used to be the view engine AND the projects it looked at. Almost
//  none of it was ever about projects: the sanitiser, the per-view document,
//  filter → sort → group, the facet counts and the revision-keyed memo would
//  work over anything. Those live in md-views.ts now, and what is left here is
//  the six answers that ARE about projects —
//
//      what fields there are          FIELDS
//      which of them make columns     GROUP_BY
//      where the rows come from       rowsFor
//      what one cell reads as         cellOf
//      what buckets an axis declares  axisBuckets
//      what the empty bucket is       EMPTY_LABEL
//
//  — plus the arithmetic only projects need: one walk of the days. A second
//  catalogue (a project's own records, md-worklist.ts) is those six answers
//  again and not one line of the engine.
//
//  Every `mdWorkshop*` name still means exactly what it meant. They are doors
//  onto md-views.ts with the catalogue already named, which is what lets the
//  Workshop's own tests keep every assertion they had.
//
//  ── this is presentation, and only presentation ───────────────────────────
//  Not one project lives here. A view is a lens; deleting every view destroys
//  nothing, and a device that has never written one still sees every project it
//  has. That is the line md-project.ts and md-pursuit.ts are on the other side
//  of, and it is why this file can be as opinionated as it likes about shape.
//
//  ── ONE DOCUMENT, NOT ONE PER VIEW ────────────────────────────────────────
//  md-database.ts splits its views per-document because a library allows 32 of
//  them, edited from several devices at once, and two people renaming two views
//  must not fight over one document. Here it is a handful of small views edited
//  by one person. The storage unit has to be the sync unit, so the unit is the
//  document, and a view is a member of it. The Workshop is UNSCOPED: one set of
//  views for the account. (md-worklist.ts is the scoped case — a set per
//  project — which is what the scope in md-views.ts exists for.)
//
//  ── THE THREE ARE MATERIALISED, NOT SEEDED ────────────────────────────────
//  List, Board and Timeline are not written at first launch; they are what
//  md-views.ts returns when the document is absent. So an account that has
//  never opened the Workshop already has them, an account syncing from an older
//  device cannot lose them, and their names travel with the code rather than
//  with the data. The same argument md-project.ts makes for Prayers.
// ════════════════════════════════════════════════════════════════════════════

type MdWorkshopViewType = 'list' | 'board' | 'timeline';
type MdWorkshopField = 'name' | 'pursuit' | 'domain' | 'status' | 'priority' | 'start' | 'target' | 'time' | 'records';
type MdWorkshopGroupBy = 'status' | 'priority' | 'pursuit' | 'domain';
type MdWorkshopZoom = 'day' | 'week' | 'month' | 'quarter';

(function (g: any) {
  'use strict';

  const CATALOGUE = 'projects';
  const KEY = 'md:projectViews';
  const DOC = 'project-views';

  const VIEW_TYPES: MdWorkshopViewType[] = ['list', 'board', 'timeline'];
  /* The order here is the order of the columns in a list, so it is a design
     decision rather than an alphabetical accident: what it is, whose it is,
     where it stands, when it runs, and how much of it there is. */
  const FIELDS: MdWorkshopField[] = ['name', 'pursuit', 'domain', 'status', 'priority', 'start', 'target', 'time', 'records'];
  /* Only these four make columns. A date does not: a board grouped by target
     is a column per day, which is a timeline drawn badly. */
  const GROUP_BY: MdWorkshopGroupBy[] = ['status', 'priority', 'pursuit', 'domain'];
  const ZOOMS: MdWorkshopZoom[] = ['day', 'week', 'month', 'quarter'];

  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
  /* `urgent · high · medium · low` is a ladder, and alphabetical
     would file High between Low and Medium. */
  const PRIORITY_ORDER: string[] = ['urgent', 'high', 'medium', 'low'];

  interface MdWorkshopTally { minutes: number; records: number }

  /* Whether work on `day` has ALREADY PASSED is md-record.ts's rule now, not a
     copy of it here — the worklist asks the same question one altitude down
     (this record's hours rather than this project's), and two copies of that
     arithmetic is how a project's card and a record's card start disagreeing
     about the same afternoon. */
  const hasPassed = (day: string, min: unknown, dur: unknown, todayKey: string, nowMin: number): boolean =>
    g.mdRecordHasPassed(day, min, dur, todayKey, nowMin);

  /** One walk of the days: id → minutes given and records held.

      mdProjectStats(id, ctx) walks every day document looking for ONE
      project's id. That was right for the dozen cards a domain held and is
      quadratic the moment a list has two thousand rows: two thousand passes
      over the same days to produce two thousand numbers one pass could have
      produced together. So the tally is built once, as a map, and every row
      reads its own entry out of it. */
  function tallyAll(ctx: any): Map<string, MdWorkshopTally> {
    const out = new Map<string, MdWorkshopTally>();
    const bump = (pid: unknown, minutes: number, records: number) => {
      if (typeof pid !== 'string' || !pid) return;
      const held = out.get(pid);
      if (held) { held.minutes += minutes; held.records += records; }
      else out.set(pid, { minutes, records });
    };

    const backlog = Array.isArray(ctx && ctx.projectRecords) ? ctx.projectRecords : [];
    for (const record of backlog) if (record) bump(record.pid, 0, 1);

    const events = (ctx && ctx.events) || {};
    const todayKey = typeof (ctx && ctx.todayKey) === 'string' ? ctx.todayKey : '';
    const nowMin = typeof (ctx && ctx.nowMin) === 'number' ? ctx.nowMin : 0;
    for (const day of Object.keys(events)) {
      if (!DAY_RE.test(day)) continue;                  // never trust a key we did not write
      const list = events[day];
      if (!Array.isArray(list)) continue;
      for (const record of list) {
        if (!record || typeof record !== 'object' || typeof record.pid !== 'string') continue;
        const dur = typeof record.dur === 'number' && Number.isFinite(record.dur) && record.dur > 0 ? record.dur : 0;
        bump(record.pid, hasPassed(day, record.min, record.dur, todayKey, nowMin) ? dur : 0, 1);
      }
    }
    return out;
  }

  /* WHAT ORDER STATUSES READ IN, asked rather than typed. Until Phase 5 this
     was a list of four words beside three other lists of the same four words;
     it is now the vocabulary itself, in the order md-workflow.ts hands it over
     — spine category first, then position. */
  function statusRows(workflowId?: string): any[] {
    const flow = g.mdWorkflowById ? g.mdWorkflowById(workflowId || 'default') : null;
    return flow ? flow.statuses : [];
  }

  /* WHERE A CATEGORY READS. Two workflows share no list of status ids — ids
     are unique inside a workflow and deliberately repeat across them, because
     a new workflow starts as a copy of the default. The only ladder they hold
     in common is the spine, so every question that must rank statuses ACROSS
     workflows is asked of the category first (WF-13). A category this build
     has never heard of reads last rather than first; md-workflow.ts makes the
     same ruling, for the same reason. */
  function spineAt(category: unknown): number {
    const spine = g.MD_SPINE || [];
    const at = spine.findIndex((row: any) => row.id === category);
    return at < 0 ? spine.length : at;
  }

  /** The one workflow every row in view reads through, or '' when they part
      company. An empty board answers with the DEFAULT rather than with the
      spine: a board of no cards still has to be a board somebody can drop the
      first card onto, and there is no disagreement to report yet. */
  function oneWorkflow(rows: any[]): string {
    let held = '';
    for (const row of rows) {
      if (!row.workflowId) continue;
      if (!held) held = row.workflowId;
      else if (held !== row.workflowId) return '';
    }
    return held || (g.MD_WORKFLOW_DEFAULT_ID || 'default');
  }

  function rowsFor(ctx: any): any[] {
    const tally = tallyAll(ctx);
    const domains: Record<string, string> = {};
    for (const domain of (g.MD_DOMAINS || [])) domains[domain.id] = domain.label;
    const pursuits: Record<string, string> = {};
    for (const pursuit of (g.mdPursuitIndex ? g.mdPursuitIndex() : [])) pursuits[pursuit.id] = pursuit.name;

    return (g.mdProjectIndex ? g.mdProjectIndex() : []).map((project: any) => {
      const held = tally.get(project.id);
      const pursuitId = typeof project.pursuitId === 'string' && pursuits[project.pursuitId]
        ? project.pursuitId : null;
      /* Resolved ONCE, here: the workflow, then the status inside it. */
      const flow = g.mdWorkflowFor ? g.mdWorkflowFor('project', project.id) : null;
      const stood = (flow && g.mdWorkflowResolveStatus
        ? g.mdWorkflowResolveStatus(flow.id, project.status) : null)
        || { id: null, name: '', category: null };
      return {
        id: project.id,
        name: project.name,
        project,
        pursuitId,
        pursuitName: pursuitId ? pursuits[pursuitId]! : '',
        domainId: project.domainId,
        domainLabel: domains[project.domainId] || '',
        /* THE RESOLVED STATUS, NOT THE STORED WORD. Everything is somewhere now
           (WF-2): a project with nothing written stands in its workflow's first
           Backlog status, and every question this pipeline answers — group,
           filter, sort, count — must be asked of where the project IS rather
           than of whether somebody has typed. Reading the raw field here is
           what used to produce a "Not saying" column.

           The NAME and the CATEGORY travel with the id, because four surfaces
           draw this row and four resolutions is three chances to forget which
           workflow the project reads through. The WORKFLOW travels too: it is
           how the board finds out whether the rows in view agree about what
           the words mean (WF-13), and `statusAt` is the tiebreak once the
           spine has ranked their categories. */
        status: stood.id,
        statusName: stood.name,
        statusCategory: stood.category,
        statusColor: stood.color || null,
        workflowId: flow ? flow.id : '',
        statusAt: flow ? flow.statuses.findIndex((row: any) => row.id === stood.id) : -1,
        priority: g.mdProjectPriority ? g.mdProjectPriority(project) : null,
        start: g.mdProjectStart ? g.mdProjectStart(project) : null,
        target: g.mdProjectTarget ? g.mdProjectTarget(project) : null,
        minutes: held ? held.minutes : 0,
        records: held ? held.records : 0,
        closed: !!project.closed,
        at: typeof project.at === 'number' ? project.at : 0,
        builtin: !!project.builtin,
      };
    });
  }

  /** What one field of one row reads as, for a filter or a sort. `null` is the
      one and only empty — md-views.ts tests for that and nothing else. */
  function cellOf(row: any, field: string): unknown {
    switch (field) {
      case 'name': return row.name || null;
      case 'pursuit': return row.pursuitId;
      case 'domain': return row.domainId || null;
      case 'status': return row.status;
      case 'priority': return row.priority;
      case 'start': return row.start;
      case 'target': return row.target;
      case 'time': return row.minutes || null;
      case 'records': return row.records || null;
      default: return null;
    }
  }

  /** The buckets of a grouping axis, IN ORDER, every one of them present
      whether or not anything is in it — a column you cannot drop onto is a
      broken promise. */
  function axisBuckets(groupBy: string, rows: any[]): { id: string; label: string }[] {
    if (groupBy === 'status') {
      /* The category rides along: a column head draws the spine's mark, and
         asking it to look the status up again is how a head and the cards
         under it end up resolved against two different workflows. */
      const only = oneWorkflow(rows || []);
      if (only) {
        return statusRows(only).map((row: any) => ({ id: row.id, label: row.name,
          category: row.category, color: row.color || null }));
      }
      /* The rows disagree, so the axis says the thing they agree ON (WF-13). A
         column is a CATEGORY here, and its id is the category's — which is what
         lets the head draw the same mark it draws for a status, untold. */
      return (g.MD_SPINE || []).map((row: any) => ({ id: row.id, label: row.label,
        category: row.id }));
    }
    if (groupBy === 'priority') {
      const labels: Record<string, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
      return PRIORITY_ORDER.map((id) => ({ id, label: labels[id]! }));
    }
    if (groupBy === 'domain') {
      return (g.MD_DOMAINS || []).map((domain: any) => ({ id: domain.id, label: domain.label }));
    }
    return (g.mdPursuitIndex ? g.mdPursuitIndex() : [])
      .map((pursuit: any) => ({ id: pursuit.id, label: pursuit.name }));
  }

  /* What a view shows when nobody has said otherwise. Domain is a field the
     engine holds and a column somebody can ask for; it is not one a table
     should draw unasked, because the lens above the table already says which
     parts of a life are in front of you and every row wears its domain's
     colour on the mark beside its name. Hidden by default, never taken away —
     the pill that brings it back is still in the Display popover. */
  const DEFAULT_FIELDS: string[] = FIELDS.filter((field) => field !== 'domain');

  /* ── the one migration this document has ever needed ──────────────────────
     Domain left the default columns when the lens arrived, and a default only
     reaches views nobody has written. So a document written before that
     release has `domain` taken off its views ONCE, on the way out, and the
     next write stamps the version. One-way and idempotent, and it retires
     itself the moment somebody says otherwise — turning the column back on is
     a write, the write carries the stamp, and a stamped document is never
     migrated again. */
  const DOC_VERSION = 1;

  function migrate(views: any[], version: number): any[] {
    if (version >= 1) return views;
    return views.map((view) => (view.visibleFields.indexOf('domain') === -1 ? view
      : { ...view, visibleFields: view.visibleFields.filter((f: string) => f !== 'domain') }));
  }

  /* THE STATUS AXIS HAS NO EMPTY BUCKET, and its absence is the point. "No
     pursuit" and "no domain" are true things a project can be; an absent
     STATUS stopped being one when absence started resolving to Inbox (WF-2).
     A column nothing can ever land in is the broken promise inverted. */
  const EMPTY_LABEL: Record<string, string> = {
    priority: 'No priority', pursuit: 'No pursuit', domain: 'No domain',
  };

  /* Fixed ids, because a view referenced from a URL or remembered as "the one
     I was on" must be the same view on the next device. */
  const DEFAULTS = () => [
    { id: 'viewlist', name: 'List', type: 'list' },
    { id: 'viewboard', name: 'Board', type: 'board', groupBy: 'status' },
    { id: 'viewtime', name: 'Timeline', type: 'timeline', timelineZoom: 'month' },
  ];

  g.mdViewsRegister({
    id: CATALOGUE,
    key: KEY,
    doc: DOC,
    fields: FIELDS,
    groupBy: GROUP_BY,
    zooms: ZOOMS,
    /* No `declared` for status: there is no single ladder to declare any more.
       Two workflows repeat each other's ids and mean different places by them,
       so the row is asked instead — spine first, then where it sits inside the
       workflow it actually reads through. Inside one workflow that is exactly
       the order it always was. */
    rank: { status: (row: any) => spineAt(row.statusCategory) * 1000
      + Math.max(row.statusAt, 0) },
    declared: { priority: PRIORITY_ORDER },
    numeric: ['time', 'records'],
    buckets: axisBuckets,
    /* Under spine the axis stopped speaking status ids, so the KEY has to stop
       too: a card is filed by the category it stands on, and keeps its own
       word for the surface to draw on the card itself. */
    bucketOf: (row: any, axis: string, rows: any[]) => (axis === 'status'
      && !oneWorkflow(rows) ? row.statusCategory : undefined),
    emptyLabel: EMPTY_LABEL,
    defaultFields: DEFAULT_FIELDS,
    /* The lens is the pursuit question, asked once above the table instead of
       row by row inside it. `'empty'` is the engine's own word for an absent
       cell — the one the facet counts and the groups already key by. */
    lensOf: (row: any) => row.pursuitId || 'empty',
    lensAxis: 'pursuit',
    version: DOC_VERSION,
    migrate,
    defaults: DEFAULTS,
    rows: rowsFor,
    cell: cellOf,
    hidden: (row: any, view: any) => !!row.closed && !view.showClosed,
    /* React hands the Workshop the same `events` and `projectRecords` objects
       until something changes them, so identity is exact and free. */
    memo: (ctx: any) => [ctx && ctx.events, ctx && ctx.projectRecords],
  });

  /* ── the doors ────────────────────────────────────────────────────────────
     Every one of these is md-views.ts with the catalogue already named. They
     are kept because `mdWorkshopRows` says which rows it means and
     `mdViewsRows('projects', …)` says it twice, and because the Workshop's
     surface and its nine test files already speak this vocabulary. */

  const mdWorkshopSanitiseView = (value: unknown) => g.mdViewsSanitise(CATALOGUE, value);
  const mdWorkshopViews = () => g.mdViewsList(CATALOGUE);
  const mdWorkshopViewById = (id: unknown) => g.mdViewsById(CATALOGUE, id);
  const mdWorkshopActiveId = () => g.mdViewsActiveId(CATALOGUE);
  const mdWorkshopSetActive = (id: unknown) => g.mdViewsSetActive(CATALOGUE, id);
  const mdWorkshopViewCreate = (type: unknown, name?: unknown, extras?: unknown) =>
    g.mdViewsCreate(CATALOGUE, type, name, extras);
  const mdWorkshopViewPatch = (id: unknown, patch: unknown) => g.mdViewsPatch(CATALOGUE, id, patch);
  const mdWorkshopViewDelete = (id: unknown) => g.mdViewsDelete(CATALOGUE, id);
  const mdWorkshopSyncDocs = () => g.mdViewsSyncDocs(CATALOGUE);
  const mdWorkshopApplyDoc = (key: unknown, value: unknown) => g.mdViewsApplyDoc(CATALOGUE, key, value);
  const mdWorkshopRows = (view: unknown, ctx?: any) => g.mdViewsRows(CATALOGUE, view, ctx);
  const mdWorkshopGroups = (view: unknown, ctx?: any) => g.mdViewsGroups(CATALOGUE, view, ctx);
  /** Which workflow the board may speak for this view, or null for the spine.
      Published because the SURFACE has to know: a spine column names a
      category, and a category means a different status id on every card under
      it. Inferring it from the shape of the column ids would be reading an
      answer rather than asking the question. */
  const mdWorkshopStatusVoice = (view: unknown, ctx?: any): string | null =>
    oneWorkflow(g.mdViewsRows(CATALOGUE, view, ctx)) || null;
  const mdWorkshopFacetCounts = (view: unknown, ctx: any, field: unknown) =>
    g.mdViewsFacetCounts(CATALOGUE, view, ctx, field);

  g.MD_WORKSHOP_KEY = KEY;
  g.MD_WORKSHOP_DOC = DOC;
  g.MD_WORKSHOP_CATALOGUE = CATALOGUE;
  g.MD_WORKSHOP_MAX_VIEWS = g.MD_VIEWS_MAX_VIEWS;
  g.MD_WORKSHOP_MAX_SORTS = g.MD_VIEWS_MAX_SORTS;
  g.MD_WORKSHOP_VIEW_TYPES = VIEW_TYPES;
  g.MD_WORKSHOP_FIELDS = FIELDS;
  g.MD_WORKSHOP_DEFAULT_FIELDS = DEFAULT_FIELDS;
  g.MD_WORKSHOP_GROUP_BY = GROUP_BY;
  g.MD_WORKSHOP_ZOOMS = ZOOMS;
  g.MD_WORKSHOP_OPERATORS = g.MD_VIEWS_OPERATORS;
  Object.assign(g, { mdWorkshopSanitiseView, mdWorkshopViews, mdWorkshopViewById,
    mdWorkshopActiveId, mdWorkshopSetActive, mdWorkshopViewCreate, mdWorkshopViewPatch,
    mdWorkshopViewDelete, mdWorkshopSyncDocs, mdWorkshopApplyDoc,
    mdWorkshopRows, mdWorkshopGroups, mdWorkshopFacetCounts,
    mdWorkshopStatusVoice });
})(typeof window !== 'undefined' ? window : globalThis);
